// O desenho da tela Nexus Neural: a teia dos agentes da IA num canvas, girando
// devagar, e os passos que a IA dá (cliente chega, recepção passa para o
// financeiro, financeiro consulta as faturas...) animados como pulsos de luz
// entre os nós. Quem chama monta a teia com a estrutura da empresa (ver
// Neuxs::Ia::Teia) e entrega cada evento com `toca`.
//
// Disposição (elipse, para parecer 3D): o núcleo no meio; a entrada embaixo,
// perto dos canais; os especialistas em volta; a equipe humana em cima; as
// ferramentas de um agente só ficam do lado de fora dele, e as de vários,
// perto do núcleo.

export const CORES = {
  acento: '#4fb3ff',
  nucleo: '#a8e4ff',
  ok: '#6fe0b8',
  equipe: '#e9b47f',
  ferramenta: '#a9bde6',
  agentes: ['#5b9bff', '#62d0ee', '#8aa6ff', '#7fb6e8', '#6fa8ff', '#8fd0f5'],
  entrada: '#9be2ff',
};

const TILT = 0.64;
const RAIO = { nucleo: 30, agente: 15, equipe: 15, ferramenta: 7, canal: 8 };
const GIRO = 0.012;
const GIRO_TV = 0.05;
// Zoom: 1 é a teia inteira cabendo na tela; quem quer ver de perto aumenta.
export const ZOOM_MINIMO = 0.5;
export const ZOOM_MAXIMO = 3;
// Até quanto o mouse anda e ainda conta como clique (não como arrastar).
const ARRASTE = 4;

const aleatorio = (a, b) => a + Math.random() * (b - a);
const sorteia = lista => lista[Math.floor(Math.random() * lista.length)];
const rgba = (hex, a) => {
  const n = parseInt(hex.slice(1), 16);
  // eslint-disable-next-line no-bitwise
  return `rgba(${n >> 16},${(n >> 8) & 255},${n & 255},${a})`;
};
const graus = g => (g * Math.PI) / 180;
const espalha = (centro, passo, i, total) =>
  centro + (i - (total - 1) / 2) * passo;

export const idDoAgente = chave => `ag:${chave}`;
export const idDaFerramenta = chave => `fe:${chave}`;
export const idDoCanal = nome => `ca:${nome}`;
export const NUCLEO = 'nucleo';
export const EQUIPE = 'equipe';

// Os nós e as ligações a partir da estrutura da empresa.
const montaNos = estrutura => {
  const nos = [
    { id: NUCLEO, tipo: 'nucleo', nome: estrutura.assistente, a: 0, r: 0 },
  ];
  const entrada = estrutura.agentes.find(agente => agente.entrada);
  const especialistas = estrutura.agentes.filter(agente => !agente.entrada);
  const angulos = {};

  if (entrada) {
    angulos[entrada.chave] = 90;
    nos.push({
      id: idDoAgente(entrada.chave),
      tipo: 'agente',
      nome: entrada.nome,
      dados: entrada,
      cor: CORES.entrada,
      a: 90,
      r: 0.6,
    });
  }
  especialistas.forEach((agente, i) => {
    const a = 150 + ((i + 0.5) * 240) / especialistas.length;
    angulos[agente.chave] = a;
    nos.push({
      id: idDoAgente(agente.chave),
      tipo: 'agente',
      nome: agente.nome,
      dados: agente,
      cor: CORES.agentes[i % CORES.agentes.length],
      a,
      r: 1,
    });
  });
  nos.push({
    id: EQUIPE,
    tipo: 'equipe',
    nome: 'Equipe humana',
    a: 270,
    r: 1.56,
    cor: CORES.equipe,
  });

  const compartilhadas = estrutura.ferramentas.filter(
    f => f.agentes.length > 1
  );
  compartilhadas.forEach((ferramenta, i) => {
    nos.push({
      id: idDaFerramenta(ferramenta.chave),
      tipo: 'ferramenta',
      nome: ferramenta.nome,
      dados: ferramenta,
      cor: CORES.ferramenta,
      a: espalha(270, 34, i, compartilhadas.length),
      r: 0.48,
    });
  });
  const porAgente = {};
  estrutura.ferramentas
    .filter(f => f.agentes.length === 1)
    .forEach(f => {
      (porAgente[f.agentes[0]] ||= []).push(f);
    });
  Object.entries(porAgente).forEach(([chave, lista]) => {
    const daEntrada = chave === entrada?.chave;
    lista.forEach((ferramenta, i) => {
      nos.push({
        id: idDaFerramenta(ferramenta.chave),
        tipo: 'ferramenta',
        nome: ferramenta.nome,
        dados: ferramenta,
        cor: CORES.ferramenta,
        a: daEntrada
          ? espalha(90, 70, i, lista.length)
          : espalha(angulos[chave] ?? 0, 16, i, lista.length),
        r: daEntrada ? 0.98 : 1.5,
      });
    });
  });

  estrutura.canais.forEach((nome, i) => {
    nos.push({
      id: idDoCanal(nome),
      tipo: 'canal',
      nome,
      a: espalha(90, 20, i, estrutura.canais.length),
      r: i === (estrutura.canais.length - 1) / 2 ? 1.62 : 1.54,
    });
  });
  return { nos, entrada: entrada?.chave };
};

export const criaMotor = (
  canvas,
  { aoSelecionar, aoMudarZoom, zoomInicial = 1 } = {}
) => {
  const ctx = canvas.getContext('2d');
  const reduz = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let nos = [];
  let porId = {};
  let entrada = null;
  const ligacoes = new Map();
  const particulas = [];
  const ondas = [];
  const agenda = [];
  let numeros = null;
  let largura = 0;
  let altura = 0;
  let cx = 0;
  let cy = 0;
  let R = 200;
  let K = 1;
  let raioBase = 200;
  let zoom = Math.min(ZOOM_MAXIMO, Math.max(ZOOM_MINIMO, zoomInicial || 1));
  let desvio = { x: 0, y: 0 };
  let arraste = null;
  let giro = 0;
  let tv = false;
  let relogio = 0;
  let quadro = null;
  let ultimo = performance.now();
  let sobre = null;
  let selecionado = null;
  const sprites = {};
  const estrelas = Array.from({ length: 170 }, () => ({
    a: aleatorio(0, Math.PI * 2),
    d: Math.sqrt(Math.random()),
    z: aleatorio(0.2, 1),
    brilho: aleatorio(0, 6),
  }));

  const depois = (ms, fn) => agenda.push({ em: relogio + ms, fn });

  const ligacao = (a, b, tipo = 'link') => {
    const chave = [a, b].sort().join('|');
    if (!ligacoes.has(chave) && porId[a] && porId[b]) {
      ligacoes.set(chave, {
        a: porId[a],
        b: porId[b],
        tipo,
        calor: 0,
        flash: 0,
      });
    }
    return ligacoes.get(chave);
  };

  const corDaLigacao = l =>
    [l.a, l.b].find(n => n.tipo === 'agente')?.cor ||
    [l.a, l.b].find(n => n.cor)?.cor ||
    CORES.acento;

  // ---------- geometria ----------
  const posiciona = () => {
    nos.forEach(n => {
      if (n.tipo === 'nucleo') {
        Object.assign(n, { x: cx, y: cy, prof: 0.5, s: 1 });
        return;
      }
      const a = graus(n.a) + giro;
      n.x = cx + Math.cos(a) * n.r * R;
      n.y = cy + Math.sin(a) * n.r * R * TILT;
      n.prof = (Math.sin(a) + 1) / 2;
      n.s = 0.78 + 0.36 * n.prof;
    });
  };

  // O zoom aumenta a teia e, um pouco menos, os nós e os textos (senão, de
  // perto, viram bolas enormes).
  const escala = () => {
    cx = largura / 2 + desvio.x;
    cy = altura / 2 + desvio.y;
    R = raioBase * zoom;
    K = Math.max(0.62, Math.min(1.2, raioBase / 240)) * Math.sqrt(zoom);
    posiciona();
  };

  const redimensiona = () => {
    const caixa = canvas.parentElement.getBoundingClientRect();
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    largura = caixa.width;
    altura = caixa.height;
    canvas.width = largura * dpr;
    canvas.height = altura * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // No celular a margem lateral diminui, senão a teia fica minúscula.
    const margem = largura < 500 ? 34 : 64;
    raioBase = Math.max(
      70,
      Math.min(
        (largura / 2 - margem) / 1.6,
        (altura / 2 - 50) / (1.6 * TILT + 0.14)
      )
    );
    escala();
  };
  const observador = new ResizeObserver(redimensiona);
  observador.observe(canvas.parentElement);

  const raio = n => RAIO[n.tipo] * K * n.s * (n.dados?.entrada ? 1.15 : 1);

  const brilho = cor => {
    if (sprites[cor]) return sprites[cor];
    const c = document.createElement('canvas');
    c.width = 128;
    c.height = 128;
    const g = c.getContext('2d');
    const gr = g.createRadialGradient(64, 64, 0, 64, 64, 64);
    gr.addColorStop(0, rgba(cor, 1));
    gr.addColorStop(0.16, rgba(cor, 0.5));
    gr.addColorStop(0.45, rgba(cor, 0.12));
    gr.addColorStop(1, rgba(cor, 0));
    g.fillStyle = gr;
    g.fillRect(0, 0, 128, 128);
    sprites[cor] = c;
    return c;
  };
  const desenhaBrilho = (cor, x, y, s, a = 1) => {
    ctx.globalAlpha = a;
    ctx.drawImage(brilho(cor), x - s, y - s, s * 2, s * 2);
    ctx.globalAlpha = 1;
  };

  const controle = l => {
    const mx = (l.a.x + l.b.x) / 2;
    const my = (l.a.y + l.b.y) / 2;
    const k = l.tipo === 'neural' ? 0 : 0.24;
    return [mx + (cx - mx) * k, my + (cy - my) * k];
  };
  const curva = (l, t) => {
    const [qx, qy] = controle(l);
    const u = 1 - t;
    return [
      u * u * l.a.x + 2 * u * t * qx + t * t * l.b.x,
      u * u * l.a.y + 2 * u * t * qy + t * t * l.b.y,
    ];
  };

  // ---------- animação dos passos ----------
  const pensa = (id, ms) => {
    const n = porId[id];
    if (n) n.pensa = Math.max(n.pensa || 0, ms);
  };
  const onda = (id, cor) => porId[id] && ondas.push({ id, cor, t: 0 });

  const envia = (de, para, opcoes = {}) => {
    const l = ligacao(de, para);
    if (!l) {
      opcoes.chega?.();
      return;
    }
    const comprimento = Math.hypot(l.a.x - l.b.x, l.a.y - l.b.y) || 200;
    particulas.push({
      l,
      ida: l.a.id === de,
      t: 0,
      dur: Math.max(0.45, comprimento / (230 * (opcoes.velocidade || 1))),
      cor: opcoes.cor || '#ffffff',
      tamanho: opcoes.tamanho || 1,
      rotulo: opcoes.rotulo,
      chega: opcoes.chega,
      rastro: [],
      ambiente: opcoes.ambiente,
    });
    l.calor += opcoes.ambiente ? 0.15 : 1;
    l.flash = 1;
    if (!opcoes.ambiente && porId[NUCLEO]) porId[NUCLEO].flash = 0.6;
  };

  // Cada passo anima e chama `segue` quando termina, para o próximo começar.
  const PASSOS = {
    chega: (p, segue) => {
      const destino = idDoAgente(p.para);
      envia(idDoCanal(p.canal), destino, {
        cor: '#eaf7ff',
        tamanho: 0.8,
        chega: () => {
          pensa(destino, 600);
          depois(500, segue);
        },
      });
    },
    passa: (p, segue) => {
      const destino = idDoAgente(p.para);
      envia(idDoAgente(p.de), destino, {
        cor: porId[destino]?.cor,
        rotulo: porId[destino]?.nome,
        tamanho: 1.15,
        velocidade: 0.75,
        chega: () => {
          pensa(destino, 800);
          depois(600, segue);
        },
      });
    },
    consulta: (p, segue) => {
      const agente = idDoAgente(p.de);
      const ferramenta = idDaFerramenta(p.ferramenta);
      envia(agente, ferramenta, {
        cor: porId[agente]?.cor,
        tamanho: 0.65,
        velocidade: 1.5,
        chega: () => {
          pensa(ferramenta, 400);
          depois(350, () =>
            envia(ferramenta, agente, {
              cor: p.erro ? CORES.equipe : '#ffffff',
              tamanho: 0.6,
              velocidade: 1.5,
              chega: () => {
                pensa(agente, 400);
                depois(300, segue);
              },
            })
          );
        },
      });
    },
    responde: (p, segue) => {
      const agente = idDoAgente(p.de);
      pensa(agente, 700);
      if (porId[agente]) porId[agente].flash = 1;
      depois(500, segue);
    },
    falha: (p, segue) => {
      onda(idDoAgente(p.de), CORES.equipe);
      depois(500, segue);
    },
    resolve: (p, segue) => {
      onda(idDoAgente(p.de), CORES.ok);
      depois(400, segue);
    },
    equipe: (p, segue) => {
      envia(idDoAgente(p.de), EQUIPE, {
        cor: CORES.equipe,
        rotulo: p.motivo,
        tamanho: 1.1,
        velocidade: 0.8,
        chega: () => {
          onda(EQUIPE, CORES.equipe);
          segue();
        },
      });
    },
  };

  const tocaPassos = (passos, aoPasso) => {
    const [passo, ...resto] = passos;
    if (!passo) return;
    try {
      aoPasso?.(passo);
    } catch {
      // Um erro de quem escuta não pode parar a animação.
    }
    const anima = PASSOS[passo.tipo];
    if (anima) anima(passo, () => tocaPassos(resto, aoPasso));
    else tocaPassos(resto, aoPasso);
  };

  // O núcleo sincronizando com os agentes, sempre: a teia nunca fica parada.
  const ambiente = () => {
    depois(aleatorio(900, 1800), () => {
      const agentes = nos.filter(n => n.tipo === 'agente');
      if (agentes.length) {
        envia(NUCLEO, sorteia(agentes).id, {
          cor: CORES.nucleo,
          tamanho: 0.45,
          velocidade: 1.2,
          ambiente: true,
        });
      }
      ambiente();
    });
  };

  // ---------- desenho ----------
  const vizinhos = id => {
    const lista = new Set([id]);
    ligacoes.forEach(l => {
      if (l.a.id === id) lista.add(l.b.id);
      if (l.b.id === id) lista.add(l.a.id);
    });
    return lista;
  };

  const forma = (n, r) => {
    ctx.beginPath();
    if (n.tipo === 'ferramenta') {
      ctx.moveTo(n.x, n.y - r);
      ctx.lineTo(n.x + r, n.y);
      ctx.lineTo(n.x, n.y + r);
      ctx.lineTo(n.x - r, n.y);
      ctx.closePath();
    } else if (n.tipo === 'equipe') {
      for (let i = 0; i < 6; i += 1) {
        const a = (Math.PI / 3) * i;
        const px = n.x + Math.cos(a) * r;
        const py = n.y + Math.sin(a) * r;
        if (i) ctx.lineTo(px, py);
        else ctx.moveTo(px, py);
      }
      ctx.closePath();
    } else ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
  };

  const etiqueta = (x, y, texto, cor, a) => {
    ctx.font = `500 ${Math.round(10.5 * Math.max(0.85, K))}px ui-monospace, SFMono-Regular, Menlo, monospace`;
    const w = ctx.measureText(texto).width + 12;
    ctx.globalAlpha = a;
    ctx.fillStyle = 'rgba(4,8,16,.82)';
    ctx.strokeStyle = cor;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.rect(x - w / 2, y - 9, w, 18);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = cor;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(texto, x, y + 0.5);
    ctx.globalAlpha = 1;
  };

  const subtitulo = n => {
    if (!numeros) return '';
    if (n.tipo === 'equipe') return `${numeros.equipe ?? 0} recebidos`;
    const dados = numeros.agentes?.[n.dados.chave];
    return `${dados?.atendidos ?? 0} atendidos`;
  };

  const desenhaFundo = agora => {
    estrelas.forEach(e => {
      const a = e.a + giro * e.z * 0.6;
      const d = e.d * Math.max(largura, altura) * 0.62;
      ctx.fillStyle = rgba(
        CORES.nucleo,
        (0.15 + 0.25 * Math.sin(agora / 900 + e.brilho) ** 2) * e.z
      );
      ctx.fillRect(
        cx + Math.cos(a) * d,
        cy + Math.sin(a) * d * 0.8,
        1.3 * e.z,
        1.3 * e.z
      );
    });
    ctx.lineWidth = 1;
    [
      [0.6, []],
      [1, [2, 6]],
      [1.55, [1, 9]],
    ].forEach(([r, traco]) => {
      ctx.setLineDash(traco);
      ctx.strokeStyle = rgba(CORES.acento, 0.09);
      ctx.beginPath();
      ctx.ellipse(cx, cy, r * R, r * R * TILT, 0, 0, Math.PI * 2);
      ctx.stroke();
    });
    ctx.setLineDash([]);
  };

  const desenhaLigacoes = (agora, foco, perto) => {
    ligacoes.forEach(l => {
      const [qx, qy] = controle(l);
      let a = 0.07 + Math.min(0.4, l.calor * 0.035) + l.flash * 0.45;
      if (perto && !(l.a.id === foco || l.b.id === foco)) a *= 0.25;
      else if (perto) a = Math.max(a, 0.45);
      ctx.strokeStyle = rgba(corDaLigacao(l), a);
      ctx.lineWidth =
        0.7 + Math.min(2.6, Math.log1p(l.calor) * 0.8) + l.flash * 1.2;
      if (l.tipo === 'neural') {
        ctx.setLineDash([3, 7]);
        ctx.lineDashOffset = -agora / 60;
      }
      ctx.beginPath();
      ctx.moveTo(l.a.x, l.a.y);
      ctx.quadraticCurveTo(qx, qy, l.b.x, l.b.y);
      ctx.stroke();
      ctx.setLineDash([]);
    });
  };

  const desenhaNucleo = (n, agora) => {
    const r = raio(n);
    const pulso = 1 + 0.06 * Math.sin(agora / 500) + (n.flash || 0) * 0.15;
    ctx.globalCompositeOperation = 'lighter';
    desenhaBrilho(CORES.acento, n.x, n.y, r * 4.2 * pulso, 0.5);
    desenhaBrilho(CORES.nucleo, n.x, n.y, r * 1.8 * pulso, 0.45);
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = 'rgba(3,8,18,.9)';
    ctx.beginPath();
    ctx.arc(n.x, n.y, r, 0, Math.PI * 2);
    ctx.fill();
    [
      [1, 1, 1.4],
      [1.35, -0.7, 1],
      [1.72, 0.45, 0.6],
    ].forEach(([k, v, w], i) => {
      const a0 = (agora / 1000) * v * (reduz ? 0.2 : 1);
      ctx.strokeStyle = rgba(i ? CORES.acento : CORES.nucleo, i ? 0.55 : 0.9);
      ctx.lineWidth = w * 1.6;
      for (let j = 0; j < 3; j += 1) {
        ctx.beginPath();
        ctx.arc(
          n.x,
          n.y,
          r * k,
          a0 + j * 2.094,
          a0 + j * 2.094 + 1.3 - i * 0.3
        );
        ctx.stroke();
      }
    });
    ctx.fillStyle = CORES.nucleo;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `700 ${Math.round(12 * K + 2)}px system-ui, sans-serif`;
    ctx.fillText('NEXUS', n.x, n.y - 1);
    ctx.font = `500 ${Math.round(9 * K + 1)}px ui-monospace, monospace`;
    ctx.fillStyle = rgba(CORES.nucleo, 0.7);
    ctx.fillText(`${numeros?.agora ?? 0} ativos`, n.x, n.y + 12 * K);
  };

  const desenhaNo = (n, agora, foco, perto) => {
    if (n.tipo === 'nucleo') {
      desenhaNucleo(n, agora);
      return;
    }
    const r = raio(n);
    const cor = n.cor || CORES.nucleo;
    const apagado = perto && !perto.has(n.id) ? 0.3 : 1;
    ctx.globalCompositeOperation = 'lighter';
    desenhaBrilho(
      cor,
      n.x,
      n.y,
      r * (n.tipo === 'ferramenta' ? 3.4 : 3) * (1 + (n.flash || 0) * 0.6),
      (0.42 + (n.pensa ? 0.3 : 0)) * apagado
    );
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = apagado;

    forma(n, r);
    if (n.tipo === 'canal') {
      ctx.fillStyle = 'rgba(3,8,18,.85)';
      ctx.fill();
      ctx.strokeStyle = cor;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    } else {
      const g = ctx.createRadialGradient(
        n.x - r * 0.35,
        n.y - r * 0.35,
        r * 0.1,
        n.x,
        n.y,
        r
      );
      g.addColorStop(0, '#ffffff');
      g.addColorStop(0.35, cor);
      g.addColorStop(1, rgba(cor, 0.35));
      ctx.fillStyle = g;
      ctx.fill();
    }
    if (n.pensa > 0) {
      const a0 = agora / 180;
      ctx.strokeStyle = cor;
      ctx.lineWidth = 1.5;
      [0, Math.PI].forEach(inicio => {
        ctx.beginPath();
        ctx.arc(n.x, n.y, r + 5, a0 + inicio, a0 + inicio + 1.6);
        ctx.stroke();
      });
    }
    if (n.id === foco) {
      ctx.strokeStyle = rgba(cor, 0.9);
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 3]);
      ctx.beginPath();
      ctx.arc(n.x, n.y, r + 10, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    const grande = n.tipo === 'agente' || n.tipo === 'equipe';
    const y = n.y + r + 6;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.font = grande
      ? `600 ${Math.round(11.5 * K + 1.5)}px system-ui, sans-serif`
      : `500 ${Math.round(9.5 * K + 1.5)}px ui-monospace, monospace`;
    ctx.fillStyle = grande ? '#eef5ff' : rgba(CORES.nucleo, 0.75);
    ctx.fillText(grande ? n.nome.toUpperCase() : n.nome, n.x, y);
    if (grande) {
      ctx.font = `400 ${Math.round(9.5 * K + 1)}px ui-monospace, monospace`;
      ctx.fillStyle = rgba(cor, 0.85);
      ctx.fillText(subtitulo(n), n.x, y + 15 * K + 2);
    }
    ctx.globalAlpha = 1;
  };

  const desenhaOndas = () => {
    ondas.forEach(o => {
      const n = porId[o.id];
      const k = o.t;
      ctx.strokeStyle = rgba(o.cor, (1 - k) * 0.85);
      ctx.lineWidth = 2 * (1 - k) + 0.5;
      ctx.beginPath();
      ctx.arc(n.x, n.y, raio(n) + k * 46 * K, 0, Math.PI * 2);
      ctx.stroke();
      ctx.globalCompositeOperation = 'lighter';
      desenhaBrilho(o.cor, n.x, n.y - k * 30, 24 * K * (1 - k), (1 - k) * 0.8);
      ctx.globalCompositeOperation = 'source-over';
    });
  };

  const desenhaParticulas = () => {
    ctx.globalCompositeOperation = 'lighter';
    particulas.forEach(p => {
      const { rastro } = p;
      for (let i = 1; i < rastro.length; i += 1) {
        ctx.strokeStyle = rgba(
          p.cor,
          (i / rastro.length) * (p.ambiente ? 0.22 : 0.65)
        );
        ctx.lineWidth = (i / rastro.length) * 3 * p.tamanho * K;
        ctx.beginPath();
        ctx.moveTo(rastro[i - 1][0], rastro[i - 1][1]);
        ctx.lineTo(rastro[i][0], rastro[i][1]);
        ctx.stroke();
      }
      const [x, y] = p.pos || [p.l.a.x, p.l.a.y];
      desenhaBrilho(p.cor, x, y, 14 * p.tamanho * K, p.ambiente ? 0.45 : 0.9);
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(x, y, 1.8 * p.tamanho * K, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.globalCompositeOperation = 'source-over';
    particulas.forEach(p => {
      if (p.rotulo && p.pos) {
        etiqueta(
          p.pos[0],
          p.pos[1] - 18 * K,
          p.rotulo,
          p.cor,
          Math.min(1, Math.sin(Math.PI * p.t) * 2)
        );
      }
    });
  };

  const desenha = agora => {
    ctx.clearRect(0, 0, largura, altura);
    const foco = sobre || selecionado;
    const perto = foco ? vizinhos(foco) : null;
    desenhaFundo(agora);
    desenhaLigacoes(agora, foco, perto);
    [...nos]
      .sort((p, q) => p.prof - q.prof)
      .forEach(n => desenhaNo(n, agora, foco, perto));
    desenhaOndas();
    desenhaParticulas();
  };

  // ---------- tempo ----------
  const avanca = dt => {
    relogio += dt * 1000;
    if (!reduz) giro += dt * (tv ? GIRO_TV : GIRO);
    posiciona();
    for (let i = agenda.length - 1; i >= 0; i -= 1) {
      if (agenda[i].em <= relogio) agenda.splice(i, 1)[0].fn();
    }
    nos.forEach(n => {
      if (n.pensa > 0) n.pensa = Math.max(0, n.pensa - dt * 1000);
      if (n.flash) n.flash = Math.max(0, n.flash - dt * 2);
    });
    ligacoes.forEach(l => {
      l.calor *= 0.965 ** dt;
      l.flash = Math.max(0, l.flash - dt * 1.6);
    });
    for (let i = ondas.length - 1; i >= 0; i -= 1) {
      ondas[i].t += dt * 0.9;
      if (ondas[i].t >= 1) ondas.splice(i, 1);
    }
    for (let i = particulas.length - 1; i >= 0; i -= 1) {
      const p = particulas[i];
      p.t = Math.min(1, p.t + dt / p.dur);
      const t = p.t < 0.5 ? 2 * p.t * p.t : 1 - (-2 * p.t + 2) ** 2 / 2;
      p.pos = curva(p.l, p.ida ? t : 1 - t);
      p.rastro.push(p.pos);
      if (p.rastro.length > (reduz ? 4 : 16)) p.rastro.shift();
      if (p.t >= 1) {
        particulas.splice(i, 1);
        (p.ida ? p.l.b : p.l.a).flash = 1;
        p.chega?.();
      }
    }
  };

  const laco = agora => {
    const dt = Math.min(0.1, (agora - ultimo) / 1000);
    ultimo = agora;
    avanca(dt);
    desenha(agora);
    quadro = requestAnimationFrame(laco);
  };

  // ---------- mouse ----------
  const alvo = evento => {
    const caixa = canvas.getBoundingClientRect();
    const x = evento.clientX - caixa.left;
    const y = evento.clientY - caixa.top;
    let melhor = null;
    let distancia = Infinity;
    nos.forEach(n => {
      const d = Math.hypot(n.x - x, n.y - y);
      if (d < raio(n) + 12 && d < distancia) {
        distancia = d;
        melhor = n.id;
      }
    });
    return melhor;
  };
  // Zoom em direção a um ponto da tela (o do mouse, na roda): o que está
  // embaixo dele fica no lugar.
  const defineZoom = (valor, px = largura / 2, py = altura / 2) => {
    const novo = Math.min(ZOOM_MAXIMO, Math.max(ZOOM_MINIMO, valor));
    const fator = novo / zoom;
    desvio = {
      x: px - largura / 2 - (px - cx) * fator,
      y: py - altura / 2 - (py - cy) * fator,
    };
    zoom = novo;
    escala();
    aoMudarZoom?.(zoom);
  };
  const posicao = evento => {
    const caixa = canvas.getBoundingClientRect();
    return [evento.clientX - caixa.left, evento.clientY - caixa.top];
  };

  const aoMover = evento => {
    if (arraste) {
      const [x, y] = posicao(evento);
      const dx = x - arraste.x;
      const dy = y - arraste.y;
      if (Math.hypot(dx, dy) > ARRASTE) arraste.moveu = true;
      if (arraste.moveu) {
        desvio = { x: arraste.desvio.x + dx, y: arraste.desvio.y + dy };
        escala();
        canvas.style.cursor = 'grabbing';
        return;
      }
    }
    sobre = alvo(evento);
    canvas.style.cursor = sobre ? 'pointer' : 'grab';
  };
  const aoSair = () => {
    sobre = null;
    arraste = null;
  };
  const aoApertar = evento => {
    const [x, y] = posicao(evento);
    arraste = { x, y, desvio: { ...desvio }, moveu: false };
  };
  const aoSoltar = () => {
    if (arraste?.moveu) canvas.style.cursor = 'grab';
  };
  const aoClicar = evento => {
    const moveu = arraste?.moveu;
    arraste = null;
    if (moveu) return;
    const id = alvo(evento);
    selecionado = id && id !== selecionado ? id : null;
    aoSelecionar?.(selecionado ? porId[selecionado] : null);
  };
  const aoRolar = evento => {
    evento.preventDefault();
    const [x, y] = posicao(evento);
    defineZoom(zoom * Math.exp(-evento.deltaY * 0.0015), x, y);
  };
  const aoDuploClique = () => {
    desvio = { x: 0, y: 0 };
    defineZoom(1);
  };
  // ---------- toque ----------
  // Um dedo rola a página (a teia ocupa quase a tela toda no celular); dois
  // dedos movem e dão zoom, e o toque num nó abre o cartão (pelo click).
  let pinca = null;
  const meioDosDedos = evento => {
    const caixa = canvas.getBoundingClientRect();
    const [a, b] = evento.touches;
    return {
      x: (a.clientX + b.clientX) / 2 - caixa.left,
      y: (a.clientY + b.clientY) / 2 - caixa.top,
      d: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
    };
  };
  const aoTocar = evento => {
    if (evento.touches.length !== 2) return;
    evento.preventDefault();
    pinca = meioDosDedos(evento);
  };
  // A cada movimento: anda o quanto o meio dos dedos andou e dá o zoom da
  // abertura em volta desse meio.
  const aoArrastarDedos = evento => {
    if (!pinca || evento.touches.length !== 2) return;
    evento.preventDefault();
    const agora = meioDosDedos(evento);
    desvio = {
      x: desvio.x + agora.x - pinca.x,
      y: desvio.y + agora.y - pinca.y,
    };
    escala();
    defineZoom(zoom * (agora.d / Math.max(pinca.d, 1)), agora.x, agora.y);
    pinca = agora;
  };
  const aoSoltarDedos = evento => {
    if (evento.touches.length < 2) pinca = null;
  };
  canvas.addEventListener('touchstart', aoTocar, { passive: false });
  canvas.addEventListener('touchmove', aoArrastarDedos, { passive: false });
  canvas.addEventListener('touchend', aoSoltarDedos);
  canvas.addEventListener('touchcancel', aoSoltarDedos);
  canvas.addEventListener('mousemove', aoMover);
  canvas.addEventListener('mouseleave', aoSair);
  canvas.addEventListener('mousedown', aoApertar);
  canvas.addEventListener('mouseup', aoSoltar);
  canvas.addEventListener('click', aoClicar);
  canvas.addEventListener('wheel', aoRolar, { passive: false });
  canvas.addEventListener('dblclick', aoDuploClique);

  return {
    monta(estrutura) {
      const montado = montaNos(estrutura);
      nos = montado.nos;
      entrada = montado.entrada;
      porId = Object.fromEntries(nos.map(n => [n.id, n]));
      ligacoes.clear();
      particulas.length = 0;
      estrutura.agentes.forEach(agente => {
        ligacao(NUCLEO, idDoAgente(agente.chave), 'neural');
        ligacao(idDoAgente(agente.chave), EQUIPE);
        if (entrada && agente.chave !== entrada) {
          ligacao(idDoAgente(entrada), idDoAgente(agente.chave));
        }
      });
      estrutura.ferramentas.forEach(f =>
        f.agentes.forEach(chave =>
          ligacao(idDoAgente(chave), idDaFerramenta(f.chave))
        )
      );
      if (entrada) {
        estrutura.canais.forEach(nome =>
          ligacao(idDoCanal(nome), idDoAgente(entrada))
        );
      }
      redimensiona();
      if (!quadro) {
        ambiente();
        quadro = requestAnimationFrame(laco);
      }
    },
    numeros(valor) {
      numeros = valor;
    },
    // Os passos de um evento, a partir de `atrasoMs`. aoPasso: para o registro.
    toca(evento, atrasoMs, aoPasso) {
      depois(atrasoMs, () => tocaPassos(evento.passos, aoPasso));
    },
    seleciona(id) {
      selecionado = id;
    },
    modoTv(ligado) {
      tv = ligado;
    },
    // Zoom pelos botões (em volta do centro da tela).
    zoom(valor) {
      defineZoom(valor);
    },
    // Volta a teia para o meio, no zoom que estiver.
    centraliza() {
      desvio = { x: 0, y: 0 };
      escala();
    },
    no: id => porId[id],
    destroi() {
      cancelAnimationFrame(quadro);
      observador.disconnect();
      canvas.removeEventListener('mousemove', aoMover);
      canvas.removeEventListener('mouseleave', aoSair);
      canvas.removeEventListener('click', aoClicar);
      canvas.removeEventListener('mousedown', aoApertar);
      canvas.removeEventListener('mouseup', aoSoltar);
      canvas.removeEventListener('wheel', aoRolar);
      canvas.removeEventListener('dblclick', aoDuploClique);
      canvas.removeEventListener('touchstart', aoTocar);
      canvas.removeEventListener('touchmove', aoArrastarDedos);
      canvas.removeEventListener('touchend', aoSoltarDedos);
      canvas.removeEventListener('touchcancel', aoSoltarDedos);
    },
  };
};

// O gráfico do pulso (mensagens por intervalo), num canvas pequeno.
export const desenhaPulso = (canvas, valores) => {
  const ctx = canvas.getContext('2d');
  const caixa = canvas.getBoundingClientRect();
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  if (canvas.width !== Math.round(caixa.width * dpr))
    canvas.width = caixa.width * dpr;
  if (canvas.height !== Math.round(caixa.height * dpr))
    canvas.height = caixa.height * dpr;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const w = caixa.width;
  const h = caixa.height;
  ctx.clearRect(0, 0, w, h);
  const maximo = Math.max(6, ...valores) * 1.15;
  const passo = w / (valores.length - 1);
  ctx.strokeStyle = rgba(CORES.acento, 0.08);
  ctx.lineWidth = 1;
  for (let i = 1; i < 4; i += 1) {
    ctx.beginPath();
    ctx.moveTo(0, (h * i) / 4);
    ctx.lineTo(w, (h * i) / 4);
    ctx.stroke();
  }
  const pontos = valores.map((v, i) => [
    i * passo,
    h - 4 - (v / maximo) * (h - 10),
  ]);
  const g = ctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0, rgba(CORES.acento, 0.35));
  g.addColorStop(1, rgba(CORES.acento, 0));
  ctx.beginPath();
  ctx.moveTo(0, h);
  pontos.forEach(([x, y]) => ctx.lineTo(x, y));
  ctx.lineTo(w, h);
  ctx.closePath();
  ctx.fillStyle = g;
  ctx.fill();
  ctx.beginPath();
  pontos.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
  ctx.strokeStyle = CORES.acento;
  ctx.lineWidth = 1.6;
  ctx.stroke();
  const [ex, ey] = pontos[pontos.length - 1];
  ctx.fillStyle = CORES.acento;
  ctx.beginPath();
  ctx.arc(ex - 2, ey, 3.5, 0, Math.PI * 2);
  ctx.fill();
};
